import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed } from "vue";
import { useApi, useToaster } from "@shopexperts/shared";
import type { ExpertTagOption } from "@shopexperts/legacy-api";

/**
 * The tag catalog is global and small (the API returns it unpaginated), so the
 * whole thing is fetched once and filtered client-side.
 */
export function useExpertTagCatalog() {
  const api = useApi();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "expert-tags"],
    queryFn: async () => await api.admin.expertManager.listTags(),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const tags = computed<ExpertTagOption[]>(() => data.value ?? []);

  const tagNameById = computed(() => {
    const map = new Map<number, string>();
    for (const tag of tags.value) map.set(tag.id, tag.name);
    return map;
  });

  const tagOptions = computed(() =>
    tags.value.map((tag) => ({ value: String(tag.id), label: tag.name })),
  );

  return { tags, tagNameById, tagOptions, isLoading, error, refetch };
}

export function useExpertTagsMutation() {
  const api = useApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();

  const syncTags = useMutation({
    mutationFn: async ({ expertId, tags }: { expertId: number; tags: string[] }) => {
      return await api.admin.expertManager.syncExpertTags({
        params: { expertId },
        body: { tags },
      });
    },
    // Returned so the mutation stays pending until the refetches land — the tag
    // editor re-renders from the refreshed expert, and awaiting mutateAsync
    // therefore means "the chips you see are current".
    onSuccess: async () => {
      toaster.create({ title: "Tags updated", type: "success" });
      await Promise.all([
        // A new tag name may have been created, so the catalog and its counts move too.
        queryClient.invalidateQueries({ queryKey: ["admin", "expert-tags"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "experts"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "expert"] }),
      ]);
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to update tags: ${error.message}`, type: "error" });
    },
  });

  return { syncTags };
}
